import React, { useState, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Modal from "aldoo-ra/Modal"
import Checkbox from "aldoo-ra/Checkbox"
import Badge from "aldoo-ra/Badge"
import Input from "aldoo-ra/Input"
import Panel from "aldoo-ra/Panel"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { api } from "aldoo-ra/API"
import {
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  MinusIcon
} from "@heroicons/react/24/outline"

// CRUD API
const crudAPI = api("crud")

// CRUD Functions
const GetResourceDefinitions = async (options = {}) => {
  const response = await crudAPI({
    get: {
      model: "PermissionDefinition",
      page: options.page || 1,
      itemsPerPage: options.itemsPerPage || 10,
      search: options.search, // Pass the search object directly
      category: options.category,
      enabledOnly: options.enabledOnly
    }
  })
  return response
}

const CreateResourceDefinition = async (resourceData) => {
  const response = await crudAPI({
    create: {
      model: "PermissionDefinition",
      data: resourceData
    }
  })
  return response
}

const AddActionToResource = async (resourceId, actionData) => {
  // First, get the current resource to read its actions array
  const currentResponse = await crudAPI({
    get: {
      model: "PermissionDefinition",
      match: { _id: resourceId }
    }
  })

  // Handle both response formats: direct array or wrapped in data property
  const responseData = currentResponse.data || currentResponse

  if (!responseData || responseData.length === 0) {
    return { result: false, error: "Resource not found" }
  }

  const currentResource = responseData[0]
  const currentActions = currentResource.actions || []

  // Check if action already exists
  if (currentActions.some(action => action.name === actionData.name)) {
    return { result: false, error: "Action already exists" }
  }

  // Add the new action to the array
  const updatedActions = [...currentActions, actionData]

  // Update using $set
  const response = await crudAPI({
    update: {
      model: "PermissionDefinition",
      match: { _id: resourceId },
      data: {
        $set: {
          actions: updatedActions
        }
      }
    }
  })

  // Handle CRUD API response format - update operations may return empty object
  if (response && (response.result !== false)) {
    return { result: true }
  } else {
    return { result: false, error: response.error || "Failed to add action" }
  }
}

const ToggleAction = async (resourceId, actionName, enabled) => {
  // First, get the current resource to read its actions array
  const currentResponse = await crudAPI({
    get: {
      model: "PermissionDefinition",
      match: { _id: resourceId }
    }
  })

  // Handle both response formats: direct array or wrapped in data property
  const responseData = currentResponse.data || currentResponse

  if (!responseData || responseData.length === 0) {
    return { result: false, error: "Resource not found" }
  }

  const currentResource = responseData[0]
  const currentActions = currentResource.actions || []

  // Update the specific action's enabled state
  const updatedActions = currentActions.map(action =>
    action.name === actionName
      ? { ...action, enabled: enabled }
      : action
  )

  // Update using $set
  const response = await crudAPI({
    update: {
      model: "PermissionDefinition",
      match: { _id: resourceId },
      data: {
        $set: {
          actions: updatedActions
        }
      }
    }
  })

  // Handle CRUD API response format - update operations may return empty object
  if (response && (response.result !== false)) {
    return { result: true }
  } else {
    return { result: false, error: response.error || "Failed to update action" }
  }
}

const UpdateAction = async (resourceId, originalActionName, updatedActionData) => {
  // First, get the current resource to read its actions array
  const currentResponse = await crudAPI({
    get: {
      model: "PermissionDefinition",
      match: { _id: resourceId }
    }
  })

  // Handle both response formats: direct array or wrapped in data property
  const responseData = currentResponse.data || currentResponse

  if (!responseData || responseData.length === 0) {
    return { result: false, error: "Resource not found" }
  }

  const currentResource = responseData[0]
  const currentActions = currentResource.actions || []

  // Update the specific action
  const updatedActions = currentActions.map(action =>
    action.name === originalActionName
      ? { ...action, ...updatedActionData }
      : action
  )

  // Update using $set
  const response = await crudAPI({
    update: {
      model: "PermissionDefinition",
      match: { _id: resourceId },
      data: {
        $set: {
          actions: updatedActions
        }
      }
    }
  })

  // Handle CRUD API response format - update operations may return empty object
  if (response && (response.result !== false)) {
    return { result: true }
  } else {
    return { result: false, error: response.error || "Failed to update action" }
  }
}

const DeleteAction = async (resourceId, actionName) => {
  // First, get the current resource to read its actions array
  const currentResponse = await crudAPI({
    get: {
      model: "PermissionDefinition",
      match: { _id: resourceId }
    }
  })

  // Handle both response formats: direct array or wrapped in data property
  const responseData = currentResponse.data || currentResponse

  if (!responseData || responseData.length === 0) {
    return { result: false, error: "Resource not found" }
  }

  const currentResource = responseData[0]
  const currentActions = currentResource.actions || []

  // Remove the action from the array
  const updatedActions = currentActions.filter(action => action.name !== actionName)

  // Update using $set
  const response = await crudAPI({
    update: {
      model: "PermissionDefinition",
      match: { _id: resourceId },
      data: {
        $set: {
          actions: updatedActions
        }
      }
    }
  })

  // Handle CRUD API response format - update operations may return empty object
  if (response && (response.result !== false)) {
    return { result: true }
  } else {
    return { result: false, error: response.error || "Failed to delete action" }
  }
}

const UpdateResourceDefinition = async (resourceId, updateData) => {
  const response = await crudAPI({
    update: {
      model: "PermissionDefinition",
      match: { _id: resourceId },
      data: {
        $set: updateData
      }
    }
  })
  return response
}

const DeleteResourceDefinition = async (resourceName) => {
  const response = await crudAPI({
    delete: {
      model: "PermissionDefinition",
      match: { resource: resourceName },
      mode: "hard"
    }
  })
  return response
}

/**
 * Resource Definitions Tab
 * Manages permission definitions/templates organized by resources and actions
 */
const ResourceDefinitionsTab = ({ refreshKey, onRefresh, onError, loading, setLoading }) => {
  // UI State
  const [expandedResources, setExpandedResources] = useState(new Set())
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showAddActionDialog, setShowAddActionDialog] = useState(false)
  const [selectedResource, setSelectedResource] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isActionEditMode, setIsActionEditMode] = useState(false)
  const [originalActionName, setOriginalActionName] = useState("")

  // Form State
  const [formData, setFormData] = useState({
    resource: "",
    description: "",
    category: "",
    actions: ""
  })
  const [actionFormData, setActionFormData] = useState({
    name: "",
    description: "",
    enabled: true
  })
  const [formError, setFormError] = useState("")


  // Handle creating new resource definition
  const handleCreateResource = useCallback(async () => {
    if (!formData.resource.trim()) {
      setFormError("Resource name is required")
      return
    }

    setFormError("")

    try {
      // Parse actions from CSV input
      const actions = formData.actions.trim()
        ? formData.actions.split(',').map(action => ({
            name: action.trim(),
            description: `${action.trim()} operations on ${formData.resource}`,
            enabled: true
          })).filter(action => action.name.length > 0)
        : []

      const result = await CreateResourceDefinition({
        resource: formData.resource.trim(),
        description: formData.description.trim() || `${formData.resource} management`,
        category: formData.category.trim() || "custom",
        actions: actions,
        isSystem: false
      })

      if (result.result) {
        setShowCreateDialog(false)
        setFormData({ resource: "", description: "", category: "", actions: "" })
        onRefresh()

        showNotification({
          message: `Resource "${formData.resource}" created successfully`,
          className: "bg-admin_success text-white"
        })
      } else {
        setFormError(result.error || "Failed to create resource")
      }
    } catch (error) {
      setFormError("Failed to create resource definition")
      console.error("Error creating resource:", error)
    }
  }, [formData, onRefresh])

  // Handle updating resource definition
  const handleUpdateResource = useCallback(async () => {
    if (!formData.resource.trim()) {
      setFormError("Resource name is required")
      return
    }

    if (!selectedResource) {
      setFormError("No resource selected for editing")
      return
    }

    setFormError("")

    try {
      const result = await UpdateResourceDefinition(selectedResource._id, {
        resource: formData.resource.trim(),
        description: formData.description.trim() || `${formData.resource} management`,
        category: formData.category.trim() || "custom"
      })

      if (result.result !== false) {
        setShowCreateDialog(false)
        setFormData({ resource: "", description: "", category: "", actions: "" })
        setSelectedResource(null)
        setIsEditMode(false)
        onRefresh()

        showNotification({
          message: `Resource "${formData.resource}" updated successfully`,
          className: "bg-admin_success text-white"
        })
      } else {
        setFormError(result.error || "Failed to update resource")
      }
    } catch (error) {
      setFormError("Failed to update resource definition")
      console.error("Error updating resource:", error)
    }
  }, [formData, selectedResource, onRefresh])

  // Handle adding action to existing resource
  const handleAddAction = useCallback(async () => {
    if (!actionFormData.name.trim()) {
      setFormError("Action name is required")
      return
    }

    setFormError("")

    try {
      const result = await AddActionToResource(selectedResource._id, {
        name: actionFormData.name.trim(),
        description: actionFormData.description.trim() || `${actionFormData.name} operations on ${selectedResource.resource}`,
        enabled: actionFormData.enabled
      })

      if (result.result) {
        setShowAddActionDialog(false)
        setActionFormData({ name: "", description: "", enabled: true })
        setSelectedResource(null)
        onRefresh()

        showNotification({
          message: `Action "${actionFormData.name}" added successfully`,
          className: "bg-admin_success text-white"
        })
      } else {
        setFormError(result.error || "Failed to add action")
      }
    } catch (error) {
      setFormError("Failed to add action")
      console.error("Error adding action:", error)
    }
  }, [actionFormData, selectedResource, onRefresh])

  // Handle updating action
  const handleUpdateAction = useCallback(async () => {
    if (!actionFormData.name.trim()) {
      setFormError("Action name is required")
      return
    }

    if (!selectedResource) {
      setFormError("No resource selected")
      return
    }

    setFormError("")

    try {
      const result = await UpdateAction(selectedResource._id, originalActionName, {
        name: actionFormData.name.trim(),
        description: actionFormData.description.trim() || `${actionFormData.name} operations on ${selectedResource.resource}`,
        enabled: actionFormData.enabled
      })

      if (result.result) {
        setShowAddActionDialog(false)
        setActionFormData({ name: "", description: "", enabled: true })
        setSelectedResource(null)
        setIsActionEditMode(false)
        setOriginalActionName("")
        onRefresh()

        showNotification({
          message: `Action "${actionFormData.name}" updated successfully`,
          className: "bg-admin_success text-white"
        })
      } else {
        setFormError(result.error || "Failed to update action")
      }
    } catch (error) {
      setFormError("Failed to update action")
      console.error("Error updating action:", error)
    }
  }, [actionFormData, selectedResource, originalActionName, onRefresh])

  // Handle toggling action enabled state
  const handleToggleAction = useCallback(async (resourceId, actionName, enabled) => {
    try {
      const result = await ToggleAction(resourceId, actionName, enabled)

      if (result.result) {
        onRefresh()
        showNotification({
          message: `Action "${actionName}" ${enabled ? 'enabled' : 'disabled'}`,
          className: "bg-admin_success text-white"
        })
      } else {
        onError(result.error || "Failed to toggle action")
      }
    } catch (error) {
      onError("Failed to toggle action")
      console.error("Error toggling action:", error)
    }
  }, [onRefresh, onError])

  // Handle deleting action
  const handleDeleteAction = useCallback(async (resourceId, actionName) => {
    try {
      const confirmed = await showPromptDialogAsync({
        title: "Delete Action",
        message: `Are you sure you want to delete the "${actionName}" action? This cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel"
      })

      if (!confirmed) return
    } catch (error) {
      console.error("Error showing prompt dialog:", error)
      return
    }

    try {
      const result = await DeleteAction(resourceId, actionName)

      if (result.result) {
        onRefresh()
        showNotification({
          message: `Action "${actionName}" deleted successfully`,
          className: "bg-admin_success text-white"
        })
      } else {
        onError(result.error || "Failed to delete action")
      }
    } catch (error) {
      onError("Failed to delete action")
      console.error("Error deleting action:", error)
    }
  }, [onRefresh, onError])

  // Handle deleting resource definition
  const handleDeleteResource = useCallback(async (resource) => {
    try {
      const confirmed = await showPromptDialogAsync({
        title: "Delete Resource",
        message: `Are you sure you want to delete the "${resource.resource}" resource and all its actions? This cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel"
      })

      if (!confirmed) return
    } catch (error) {
      console.error("Error showing prompt dialog:", error)
      return
    }

    try {
      const result = await DeleteResourceDefinition(resource.resource)

      if (result.result) {
        onRefresh()
        showNotification({
          message: `Resource "${resource.resource}" deleted successfully`,
          className: "bg-admin_success text-white"
        })
      } else {
        onError(result.error || "Failed to delete resource")
      }
    } catch (error) {
      onError("Failed to delete resource")
      console.error("Error deleting resource:", error)
    }
  }, [onRefresh, onError])

  // DataTable renders (ParamIO style)
  const renders = [
    {
      id: "resource",
      render: (_, resource) => {
        return (
          <div className="space-y-0">
            <Panel
              variant="admin_bordered_shadow"
              defaultOpen={expandedResources.has(resource.resource)}
              collapsible={true}
              styleOverrides={{
                header: "flex items-center bg-admin_neutral_1/50  text-admin_text cursor-pointer p-4",
                panel: "border border-admin_bordered rounded-lg overflow-hidden"
              }}
              className="w-full"
              title={
                <div className="flex items-center space-x-3 flex-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <Tooltip
                        content={resource.description || `${resource.resource} management`}
                      >
                        <Typography 
                          variant="admin_label" 
                          className="my-0"
                        >
                          {resource.resource}
                        </Typography>
                      </Tooltip>

                      <Badge
                        variant="admin_default"
                      >
                        {resource.actions?.length || 0} actions
                      </Badge>
                    </div>
                  </div>
                </div>
              }
              headerContent={
                expandedResources.has(resource.resource) ? (
                  <div className="flex items-center space-x-2">
                    <Tooltip
                      content={`Add new action to ${resource.resource}`}
                      variant="admin"
                    >
                      <button
                        className="w-8 h-8 rounded-full p-0 flex items-center justify-center bg-admin_primary text-white hover:bg-admin_primary/80 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedResource(resource)
                          setShowAddActionDialog(true)
                        }}
                      >
                        <PlusCircleIcon className="w-5 h-5" />
                      </button>
                    </Tooltip>

                    <Tooltip
                      content={`Edit ${resource.resource} permission`}
                      variant="admin"
                    >
                      <button
                        className="w-8 h-8 rounded-full p-0 flex items-center justify-center bg-admin_warning text-white hover:bg-admin_warning/80 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedResource(resource)
                          setFormData({
                            resource: resource.resource,
                            description: resource.description || "",
                            category: resource.category || "",
                            actions: ""
                          })
                          setIsEditMode(true)
                          setShowCreateDialog(true)
                        }}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </Tooltip>

                    <Tooltip
                      content={`Delete ${resource.resource} permission and all its actions`}
                      variant="admin"
                    >
                      <button
                        className="w-8 h-8 rounded-full p-0 flex items-center justify-center bg-admin_error text-white hover:bg-admin_error/80 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteResource(resource)
                        }}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </div>
                ) : null
              }
              onToggle={(isOpen) => {
                setExpandedResources(prev => {
                  const newSet = new Set(prev)
                  if (isOpen) {
                    newSet.add(resource.resource)
                  } else {
                    newSet.delete(resource.resource)
                  }
                  return newSet
                })
              }}
            >
              <div>
                {resource.actions?.length > 0 ? (
                  <div>
                    {/* Actions List */}
                    <div className="divide-y divide-admin_bordered">
                      {resource.actions.map(action => (
                        <div
                          key={action.name}
                          className={`flex items-center justify-between py-2 pl-4 ${
                            action.enabled
                              ? 'hover:bg-admin_neutral_1/20'
                              : 'bg-admin_error/5 hover:bg-admin_error/10'
                          }`}
                        >
                          {/* Left side - Name */}
                          <div className="flex-1 min-w-0">
                            <Tooltip
                              content={action.description || `${action.name} operations on ${resource.resource}`}
                            >
                              <Typography 
                                variant="admin_txt1" 
                                className="font-medium text-admin_text"
                              >
                                {action.name}
                              </Typography>
                            </Tooltip>
                          </div>
                          
                          {/* Right side - Actions */}
                          <div className="flex items-center space-x-3 ml-4">
                            <div className="flex items-center space-x-1 mr-3">
                              <Typography variant="admin_txt1_dim">
                                Enable:
                              </Typography>

                              <Checkbox
                                variant="admin"
                                isOn={action.enabled}
                                onChange={(enabled) => handleToggleAction(resource._id, action.name, enabled)}
                                className="transition-none"
                              />
                            </div>
                            
                            <Tooltip content="Edit action">
                              <Button
                                variant="admin_icon"
                                onClick={() => {
                                  setSelectedResource(resource)
                                  setActionFormData({
                                    name: action.name,
                                    description: action.description || "",
                                    enabled: action.enabled
                                  })
                                  setOriginalActionName(action.name)
                                  setIsActionEditMode(true)
                                  setShowAddActionDialog(true)
                                }}
                              >
                                <PencilIcon className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                            
                            <Tooltip content="Delete action">
                              <Button
                                variant="admin_icon"
                                className="hover:text-admin_error"
                                onClick={() => handleDeleteAction(resource._id, action.name)}
                              >
                                <TrashIcon className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-admin_neutral_1/50 rounded-full mx-auto mb-3">
                      <ShieldCheckIcon className="w-6 h-6 text-admin_text_secondary" />
                    </div>

                    <Typography 
                      variant="admin_txt2_dim" 
                      className="block lead-0 mb-2"
                    >
                      No actions defined for this resource.
                    </Typography>

                    <Typography 
                      variant="admin_txt2_dim" 
                      className="block lead-0"
                    >
                      Add actions to define what operations are available.
                    </Typography>
                  </div>
                )}

              </div>
            </Panel>
          </div>
        )
      },
    },
  ]


  return (
    <div>
      {/* DataTable with Simple CRUD API */}
      <div className="bg-admin_paper rounded-lg py-4">
        <DataTable
          variant="admin_simple"
          apiMethod={GetResourceDefinitions}
          apiDataField="data"
          localUserPreferencesKey="admin.platformAccess.resourceDefinitions"
          renders={renders}
          refreshKey={refreshKey}
          preferredHeight="82vh"
          itemHeight="h-[auto]"
          itemsPerPage={15}
          showSearch={true}
          searchField="resource description category actions.name"
          searchPlaceholder="Search permissions, descriptions, categories, or actions..."
          headerControls={
            <div className="flex items-center justify-end">
              <Button 
                variant="admin_primary" 
                onClick={() => setShowCreateDialog(true)}
              >
                <PlusCircleIcon className="w-5 h-5 mr-1" />
                Permission
              </Button>
            </div>
          }
          styleOverrides={{
            searchInput:"ml-0 flex-1 min-w-[300px]",
          }}
          noData={
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center bg-admin_neutral_1/50 rounded-full mb-4 mx-auto">
                <ShieldCheckIcon className="w-8 h-8 text-admin_text_secondary" />
              </div>

              <Typography variant="admin_body" className="mb-2">
                No permissions found
              </Typography>

              <Typography variant="admin_txt1_dim">
                Create your first permission to get started with permission management
              </Typography>

              <Button
                variant="admin_primary"
                onClick={() => setShowCreateDialog(true)}
                className="mt-4 mx-auto"
              >
                <PlusCircleIcon className="w-5 h-5 mr-1" />
                Permission
              </Button>
            </div>
          }
        />
      </div>

      {/* Create Resource Dialog */}
      <Modal
        isOpen={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false)
          setFormData({ resource: "", description: "", category: "", actions: "" })
          setFormError("")
          setSelectedResource(null)
          setIsEditMode(false)
        }}
        title={isEditMode ? "Edit Permission" : "Create Permission"}
        variant="admin_centered"
        className="max-w-2xl"
        footerContent={
          <div className="flex justify-end space-x-3">
            <Button
              variant="admin_secondary"
              onClick={() => {
                setShowCreateDialog(false)
                setFormData({ resource: "", description: "", category: "", actions: "" })
                setFormError("")
                setSelectedResource(null)
                setIsEditMode(false)
              }}
            >
              Cancel
            </Button>

            <Button
              variant="admin_primary"
              onClick={isEditMode ? handleUpdateResource : handleCreateResource}
              disabled={loading || !formData.resource.trim()}
            >
              {isEditMode ? "Update Permission" : "Create Permission"}
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Permission Information Section */}
          <div className="space-y-4">
            <div>
              <Typography 
                variant="admin_label" 
                className="mb-0.5"
              >
                Permission Name *
              </Typography>

              <Input
                placeholder="e.g., user, post, product"
                value={formData.resource}
                onChange={(e) => setFormData(prev => ({ ...prev, resource: e.target.value }))}
                variant="admin"
                className="w-full"
              />

              <Typography variant="admin_txt2_dim">
                Use lowercase, singular nouns for consistency
              </Typography>
            </div>

            <div>
              <Typography 
                variant="admin_label" 
                className="mb-0.5"
              >
                Description
              </Typography>

              <Input
                placeholder="Describe what this permission represents"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                variant="admin"
                className="w-full"
                multiline
                rows={2}
              />
            </div>

            <div>
              <Typography 
                variant="admin_label" 
                className="mb-0.5"
              >
                Category
              </Typography>

              <Input
                placeholder="e.g., user_management, content, system"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                variant="admin"
                className="w-full"
              />

              <Typography variant="admin_txt2_dim">
                Helps organize related permissions
              </Typography>
            </div>
          </div>

          {/* Actions Section - Only show in create mode */}
          {!isEditMode && (
            <div>
              <div>
                <Typography 
                  variant="admin_label" 
                  className="mb-0.5"
                >
                  Initial Actions (Optional)
                </Typography>

                <Input
                  placeholder="e.g., read, write, delete, manage, ..."
                  value={formData.actions}
                  onChange={(e) => setFormData(prev => ({ ...prev, actions: e.target.value }))}
                  variant="admin"
                  className="w-full"
                />

                <Typography variant="admin_txt2_dim">
                  Common actions: read, write, delete, create, update, manage
                </Typography>
              </div>
            </div>
          )}

          {formError && (
            <div className=" flex items-start gap-2 bg-admin_error/10 border border-admin_error/20 rounded-lg p-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-admin_error flex-shrink-0 mt-0.5" />
              <Typography 
                variant="admin_txt2" 
                className="text-admin_error"
              >
                {formError}
              </Typography>
            </div>
          )}
        </div>
      </Modal>

      {/* Add Action Dialog */}
      <Modal
        isOpen={showAddActionDialog}
        onClose={() => {
          setShowAddActionDialog(false)
          setActionFormData({ name: "", description: "", enabled: true })
          setFormError("")
          setSelectedResource(null)
          setIsActionEditMode(false)
          setOriginalActionName("")
        }}
        title={isActionEditMode ? `Edit Action in ${selectedResource?.resource}` : `Add Action to ${selectedResource?.resource}`}
        variant="admin_centered"
        className="max-w-lg"
        footerContent={
          <div className="flex justify-end space-x-3">
            <Button
              variant="admin_secondary"
              onClick={() => {
                setShowAddActionDialog(false)
                setActionFormData({ name: "", description: "", enabled: true })
                setFormError("")
                setSelectedResource(null)
                setIsActionEditMode(false)
                setOriginalActionName("")
              }}
            >
              Cancel
            </Button>

            <Button
              variant="admin_primary"
              onClick={isActionEditMode ? handleUpdateAction : handleAddAction}
              disabled={loading || !actionFormData.name.trim()}
            >
              {isActionEditMode ? "Update Action" : "Add Action"}
            </Button>
          </div>
        }
      >
        <div className="space-y-5 -mt-6">
          <div>
            <Typography 
              variant="admin_label" 
              className="mb-0.5"
            >
              Action Name *
            </Typography>

            <Input
              placeholder="e.g., read, write, delete, manage"
              value={actionFormData.name}
              onChange={(e) => setActionFormData(prev => ({ ...prev, name: e.target.value }))}
              variant="admin"
              className="w-full"
            />

            <Typography variant="admin_txt2_dim">
              Use lowercase, verb-based names for consistency
            </Typography>
          </div>

          <div>
            <Typography 
              variant="admin_label" 
              className="mb-0.5"
            >
              Description
            </Typography>

            <Input
              placeholder="Describe what this action allows users to do"
              value={actionFormData.description}
              onChange={(e) => setActionFormData(prev => ({ ...prev, description: e.target.value }))}
              variant="admin"
              className="w-full"
              multiline
              rows={2}
            />
          </div>


          {formError && (
            <div className="flex items-start gap-2 bg-admin_error/10 border border-admin_error/20 rounded-lg p-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-admin_error flex-shrink-0 mt-0.5" />
              <Typography 
                variant="admin_txt2" 
                className="text-admin_error"
              >
                {formError}
              </Typography>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default ResourceDefinitionsTab